import { getSupplier } from '@/utils/stateAccessors';

import { formatPhoneNumber } from '@payaca/helpers/phoneNumberHelper';

import { FC, useCallback, useMemo, type JSX } from 'react';

import { useSelector } from '@/api/state';
import GlobeIcon from '@payaca/components/svgAssets/GlobeIcon';
import GoogleMapAnchor from '@payaca/components/googleMapAnchor/GoogleMapAnchor';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { SupplierAddress } from '@payaca/types/supplierTypes';
import './SupplierContactInformation.sass';

import UntitledIcon from '@payaca/untitled-icons';
type Props = {
  supplierId: number;
};
const SupplierContactInformation: FC<Props> = ({
  supplierId,
}: Props): JSX.Element | null => {
  const supplier = useSelector((state) => getSupplier(state, supplierId));

  const supplierTelephoneElement = useMemo(() => {
    if (supplier?.phoneNumber?.length) {
      const formattedTelephoneNumber = formatPhoneNumber(supplier.phoneNumber);
      return (
        <div className="supplier-information-element">
          <a
            href={`tel:${formattedTelephoneNumber}`}
            target="_blank"
            rel="noreferrer"
          >
            <UntitledIcon name="phone.3" />
            <span>{formattedTelephoneNumber}</span>
          </a>
        </div>
      );
    }
  }, [supplier]);

  const supplierEmailElement = useMemo(() => {
    if (supplier?.emailAddress?.length) {
      return (
        <div className="supplier-information-element">
          <a
            href={`mailto:${supplier.emailAddress}`}
            target="_blank"
            rel="noreferrer"
          >
            <UntitledIcon name="message-text-square-01.3" />
            <span>{supplier.emailAddress}</span>
          </a>
        </div>
      );
    }
  }, [supplier]);

  const supplierWebsiteUrlElement = useMemo(() => {
    if (supplier?.websiteUrl?.length) {
      let navigateTo = supplier.websiteUrl;
      try {
        const u = new URL(supplier.websiteUrl);
      } catch (error) {
        navigateTo = `//${supplier.websiteUrl}`;
      }

      return (
        <div className="supplier-information-element">
          <a href={navigateTo} target="_blank" rel="noreferrer">
            <GlobeIcon />
            <span>{supplier.websiteUrl}</span>
          </a>
        </div>
      );
    }
  }, [supplier]);

  const renderSupplierAddressElement = useCallback(
    (supplierAddress: SupplierAddress, index: number) => {
      const addressAsString = getAddressAsString(supplierAddress.address);

      if (!addressAsString?.length) return;

      return (
        <div className="supplier-information-element">
          <GoogleMapAnchor
            coordinates={supplierAddress.address}
            addressText={addressAsString}
          >
            <UntitledIcon name="marker-pin-01.3" />
            <span>
              {supplierAddress.description &&
                `${supplierAddress.description} | `}
              {addressAsString}
            </span>
          </GoogleMapAnchor>
        </div>
      );
    },
    []
  );

  if (!supplier) return null;
  return (
    <div className="supplier-contact-information">
      {supplierEmailElement}
      {supplierTelephoneElement}
      {supplierWebsiteUrlElement}
      {supplier.addresses?.map(renderSupplierAddressElement)}
    </div>
  );
};

export default SupplierContactInformation;
